import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Container,
  Slide,
  alpha
} from '@mui/material';
import {
  ShoppingCart as OrderIcon,

  // Receipt as ReceiptIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import { Container as ContainerType, ContainerResponse } from '../../types/container';
import { containerService } from '../../services/api';
import { useLocation } from 'react-router-dom';

const ContainerList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ containers, setContainers ] = useState<ContainerType[]>( [] );
  const [ loading, setLoading ] = useState( false );
  const [ toast, setToast ] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>( {
    open: false,
    message: '',
    severity: 'success'
  } );
  const [ page, setPage ] = useState( 0 );
  const [ rowsPerPage, setRowsPerPage ] = useState( 10 );
  const [ searchInput, setSearchInput ] = useState( '' );
  const [ search, setSearch ] = useState( '' ); // keep this for API
  const debounceTimeout = useRef<NodeJS.Timeout | null>( null );
  const [ totalCount, setTotalCount ] = useState( 0 );
  const [ deleteDialogOpen, setDeleteDialogOpen ] = useState( false );
  const [ containerToDelete, setContainerToDelete ] = useState<ContainerType | null>( null );
  const [ statusDialogOpen, setStatusDialogOpen ] = useState( false );
  const [ containerToUpdate, setContainerToUpdate ] = useState<{ container: ContainerType; newStatus: string } | null>( null );
  const location = useLocation();
  const initialFilter = location.state?.filter || 'all';
  const [ statusFilter, setStatusFilter ] = useState<string>( initialFilter );

  useEffect( () => {
    fetchContainers();
  }, [ page, rowsPerPage, search, statusFilter ] );

  const fetchContainers = async () => {
    try {
      setLoading( true );
      const response: ContainerResponse = await containerService.getContainers( {
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        status: statusFilter || undefined
      } );

      if ( response.success ) {
        setContainers( response.data );
        setTotalCount( response.pagination.total );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'containers.error.fetch' ),
          severity: 'error'
        } );
      }
    } catch ( err ) {
      console.error( 'Error fetching containers:', err );
      setToast( {
        open: true,
        message: t( 'containers.error.fetch' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
    }
  };

  const handleChangePage = ( event: unknown, newPage: number ) => {
    setPage( newPage );
  };

  const handleChangeRowsPerPage = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    setRowsPerPage( parseInt( event.target.value, 10 ) );
    setPage( 0 );
  };

  // Debounce and min length effect
  useEffect( () => {
    if ( debounceTimeout.current ) {
      clearTimeout( debounceTimeout.current );
    }
    debounceTimeout.current = setTimeout( () => {
      if ( searchInput.length === 0 || searchInput.length >= 3 ) {
        setSearch( searchInput );
      }
    }, 300 );
    return () => {
      if ( debounceTimeout.current ) {
        clearTimeout( debounceTimeout.current );
      }
    };
  }, [ searchInput ] );

  const handleDeleteClick = ( container: ContainerType ) => {
    setContainerToDelete( container );
    setDeleteDialogOpen( true );
  };

  const handleDeleteConfirm = async () => {
    if ( !containerToDelete ) return;

    try {
      setLoading( true );
      const response = await containerService.updateContainerStatus( {
        containerId: containerToDelete.id,
        status: 'delete'
      } );

      if ( response.success ) {
        fetchContainers();
        setToast( {
          open: true,
          message: t( 'containers.success.delete' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'containers.error.delete' ),
          severity: 'error'
        } );
      }
    } catch ( err ) {
      console.error( 'Error deleting container:', err );
      setToast( {
        open: true,
        message: t( 'containers.error.delete' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
      setDeleteDialogOpen( false );
      setContainerToDelete( null );
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen( false );
  };

  const handleStatusClick = ( container: ContainerType, newStatus: string ) => {
    setContainerToUpdate( { container, newStatus } );
    setStatusDialogOpen( true );
  };

  const handleStatusConfirm = async () => {
    if ( !containerToUpdate ) return;

    try {
      setLoading( true );
      const response = await containerService.updateContainerStatus( {
        containerId: containerToUpdate.container.id,
        status: containerToUpdate.newStatus
      } );

      if ( response.success ) {
        fetchContainers();
        setToast( {
          open: true,
          message: t( 'containers.success.status' ),
          severity: 'success'
        } );
      } else {
        setToast( {
          open: true,
          message: response.message || t( 'containers.error.status' ),
          severity: 'error'
        } );
      }
    } catch ( err ) {
      console.error( 'Error updating container status:', err );
      setToast( {
        open: true,
        message: t( 'containers.error.status' ),
        severity: 'error'
      } );
    } finally {
      setLoading( false );
      setStatusDialogOpen( false );
      setContainerToUpdate( null );
    }
  };

  const handleStatusCancel = () => {
    setStatusDialogOpen( false );
    setContainerToUpdate( null );
  };

  const handleCloseToast = () => {
    setToast( { ...toast, open: false } );
  };

  if ( loading && containers.length === 0 ) {
    return (
      <Container maxWidth="lg" sx={ { py: 4 } }>
        <Box sx={ {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          gap: 2
        } }>
          <CircularProgress size={ 60 } />
          <Typography variant="h6" color="text.secondary">
            Loading containers...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={ false } sx={ { py: 4, width: '100%' } }>
      <Slide direction="down" in={ true } timeout={ 600 }>
        <Box>
          {/* Header Section */ }
          <Box sx={ { mb: 4 } }>
            <Stack direction="row" alignItems="center" spacing={ 2 } sx={ { mb: 2 } }>
              <Box sx={ {
                p: 1.5,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              } }>
                <StorageIcon sx={ { fontSize: 28 } } />
              </Box>
              <Box>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  { t( 'containers.title' ) }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Manage and monitor your storage containers
                </Typography>
              </Box>
            </Stack>

            <Box sx={ { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 } }>
              <Chip
                icon={ <StorageIcon /> }
                label={ `${ totalCount } containers` }
                color="primary"
                variant="outlined"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={ <AddIcon /> }
                onClick={ () => navigate( '/lockers/add' ) }
                sx={ {
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)'
                  }
                } }
              >
                { t( 'containers.management.addContainer' ) }
              </Button>
            </Box>

            {/* Search Bar */ }
            <TextField
              fullWidth
              placeholder="Search containers by name, ID, or location..."
              value={ searchInput }

              onChange={ ( e ) => setSearchInput( e.target.value ) }
              InputProps={ {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              } }
              sx={ {
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'background.paper'
                }
              } }
            />
          </Box>

          {/* Containers Table */ }
          <Paper
            elevation={ 0 }
            sx={ {
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden'
            } }
          >
            { containers.length === 0 ? (

              <Box sx={ {
                textAlign: 'center',
                py: 8,
                color: 'text.secondary'
              } }>
                <OrderIcon sx={ { fontSize: 64, mb: 2, opacity: 0.5 } } />
                <Typography variant="h6" sx={ { mb: 1 } }>
                  No orders found
                </Typography>
                <Typography variant="body2">
                  { searchInput ? 'Try adjusting your search criteria' : 'No orders available' }
                </Typography>
              </Box>

            ) : ( <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={ {
                      backgroundColor: 'primary.main',
                      '& th': {
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }
                    } }>
                      <TableCell align="center">
                        { "ID" }
                      </TableCell>
                      <TableCell align="left">
                        { t( 'containers.management.containerTitle' ) }
                      </TableCell>
                      <TableCell align="left">
                        { t( 'containers.management.location' ) }
                      </TableCell>
                      <TableCell align="center">
                        { t( 'containers.management.occupiedLockers' ) }
                      </TableCell>
                      <TableCell align="center">
                        { t( 'common.status' ) }
                      </TableCell>
                      <TableCell align="center">
                        { t( 'containers.management.actions' ) }
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    { containers.map( ( container ) => (
                      <TableRow
                        key={ container.id }
                        sx={ {
                          '&:hover': {
                            backgroundColor: ( theme ) => alpha( theme.palette.primary.main, 0.04 )
                          },
                          transition: 'background-color 0.2s ease'
                        } }
                      >
                        <TableCell align="center">
                          <Typography variant="subtitle2" >
                            { container.containerNumber }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" >
                            { container.containerName }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            { container.address + "/" + container.zipCode }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ `${ container.occupiedLockerCount } / ${ container.lockerCount }` }
                            color={ parseInt( container.occupiedLockerCount ) > 0 ? 'primary' : 'default' }
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={ container.status === 'active' ? 'Active' : 'Inactive' }
                            color={ container.status === 'active' ? 'success' : 'error' }
                            size="small"
                            onClick={ () => handleStatusClick( container, container.status === 'active' ? 'inactive' : 'active' ) }
                            clickable
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={ 1 } justifyContent="center">
                            <Tooltip title={ t( 'common.view' ) } arrow>
                              <IconButton
                                onClick={ () => navigate( `/lockers/${ container.id }/view` ) }
                                size="small"
                                color="primary"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={ t( 'common.edit' ) } arrow>
                              <IconButton
                                onClick={ () => navigate( `/lockers/${ container.id.toString() }` ) }
                                size="small"
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={ t( 'common.delete' ) } arrow>
                              <IconButton
                                onClick={ () => handleDeleteClick( container ) }
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ) ) }
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={ [ 5, 10, 25 ] }
                  component="div"
                  count={ totalCount }
                  rowsPerPage={ rowsPerPage }
                  page={ page }
                  onPageChange={ handleChangePage }
                  onRowsPerPageChange={ handleChangeRowsPerPage }
                />
              </TableContainer>
            </> ) }

            {/* {containers.length === 0 && !loading && (
              <Box sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary'
              }}>
                <StorageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  No containers found
                </Typography>
                <Typography variant="body2">
                  {search ? 'Try adjusting your search criteria' : 'Get started by adding your first container'}
                </Typography>
              </Box>
            )} */}

          </Paper>
        </Box>
      </Slide>

      {/* Delete Confirmation Dialog */ }
      <Dialog
        open={ deleteDialogOpen }
        onClose={ handleDeleteCancel }
        maxWidth="sm"
        fullWidth
      >
      <DialogTitle sx={ {
               bgcolor: 'error.main',
               color: 'white',
               display: 'flex',
               alignItems: 'center',
               gap: 2,
               py: 2.5,
               px: 3
             } }>
               <DeleteIcon />
               Delete Container
             </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>
          { containerToDelete && (
            <Typography>
              { t( 'containers.delete.confirmation', { name: containerToDelete.containerName } ) }
            </Typography>
          ) }
        </DialogContent>
        <DialogActions sx={ { p: 3, g: 2 } }>
          <Button onClick={ handleDeleteCancel } disabled={ loading } variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }>
            { t( 'common.cancel' ) }
          </Button>
          <Button
            onClick={ handleDeleteConfirm }
            color="error"
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? <CircularProgress size={ 24 } /> : t( 'common.delete' ) }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Update Dialog */ }
      <Dialog
        open={ statusDialogOpen }
        onClose={ handleStatusCancel }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={ {
          bgcolor: containerToUpdate?.newStatus === 'active' ? 'success.main' : 'error.main',
          textTransform: 'none',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 2.5,
          px: 3
        } }>
          { containerToUpdate?.newStatus === 'active' ? 'Activate Container' : 'Deactivate Container' }
        </DialogTitle>
        <DialogContent style={ { paddingTop: "20px" } }>
          { containerToUpdate && (
            <Typography>
              Are you sure you want to { containerToUpdate.newStatus === 'active' ? 'activate' : 'deactivate' } container "{ containerToUpdate.container.containerName }"?
            </Typography>
          ) }
        </DialogContent>
        <DialogActions sx={ { p: 3, gap: 2 } } >
          <Button onClick={ handleStatusCancel } disabled={ loading } variant="outlined"
            sx={ {
              minWidth: 100,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }>
            Cancel
          </Button>
          <Button
            onClick={ handleStatusConfirm }
            color={ containerToUpdate?.newStatus === 'active' ? 'success' : 'error' }
            variant="contained"
            disabled={ loading }
            sx={ {
              minWidth: 120,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            } }
          >
            { loading ? <CircularProgress size={ 24 } /> : ( containerToUpdate?.newStatus === 'active' ? 'Activate' : 'Deactivate' ) }
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={ toast.open }
        autoHideDuration={ 6000 }
        onClose={ handleCloseToast }
        anchorOrigin={ { vertical: 'top', horizontal: 'right' } }
      >
        <Alert onClose={ handleCloseToast } severity={ toast.severity } sx={ { width: '100%' } }>
          { toast.message }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ContainerList;
